import { useTranslations } from '@aikoa/i18n/react'
import { useState } from 'react'
import { Modal, TouchableOpacity, View } from 'react-native'
import DateTimePicker, { useDefaultClassNames, type DateType } from 'react-native-ui-datepicker'
import CloseButton from '~/components/CloseButton.tsx'
import { DisplayValue, Label } from '~/components/typography/Content.tsx'
import { Button } from '~/components/ui/button.tsx'
import { Text } from '~/components/ui/text'
import { getDateFromDateType } from '~/lib/dateType.ts'
import { cn } from '~/lib/utils.ts'
import Icon from './Icon'

interface DatePickerProps {
  value?: Date
  onDateChange: (date: Date | undefined) => void
  placeholder?: string
  label?: string
  disabled?: boolean
  minDate?: DateType
  maxDate?: DateType
  className?: string
  enableFuture?: boolean
}

export function DatePicker({
  value,
  onDateChange,
  placeholder,
  label,
  disabled = false,
  minDate,
  maxDate,
  className,
}: DatePickerProps) {
  const { t } = useTranslations('common')
  const defaultPlaceholder = t(($) => $.datePicker.placeholder)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedDate, setSelectedDate] = useState<DateType>(value || undefined)

  const defaultClassNames = useDefaultClassNames()

  const formatDate = (date: Date | undefined) => {
    if (!date) {
      return placeholder ?? defaultPlaceholder
    }

    return new Date(date).toLocaleDateString('fi-FI')
  }

  const handleDateSelect = ({ date }: { date: DateType }) => {
    setSelectedDate(date)
  }

  const handleConfirm = () => {
    const dateValue = getDateFromDateType(selectedDate)
    onDateChange(dateValue)
    setIsModalVisible(false)
  }

  const handleCancel = () => {
    setSelectedDate(value || undefined)
    setIsModalVisible(false)
    return false
  }

  return (
    <View className={cn('gap-2', className)}>
      {label && <Label>{label}</Label>}

      <TouchableOpacity
        onPress={() => !disabled && setIsModalVisible(true)}
        disabled={disabled}
        className={cn(
          'flex-row items-center justify-between rounded-lg border-0 bg-muted px-3 py-3',
          disabled && 'opacity-50'
        )}>
        <DisplayValue className={cn('flex-1 text-foreground')}>{formatDate(value)}</DisplayValue>
        <Icon name="calendar" size={20} className="text-foreground" />
      </TouchableOpacity>

      <Modal
        visible={isModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCancel}>
        <View className="flex-1 items-center justify-center bg-black/50 px-4">
          <View className="w-full max-w-sm rounded-lg bg-background p-4 shadow-lg">
            <View className="mb-4 flex-row items-center justify-between">
              <Text className="font-semibold text-lg text-foreground">
                {t(($) => $.datePicker.title)}
              </Text>
              <CloseButton onPress={handleCancel} className="-right-1 -top-1 size-8" />
            </View>

            <DateTimePicker
              mode="single"
              date={selectedDate || undefined}
              onChange={handleDateSelect}
              minDate={minDate}
              maxDate={maxDate}
              // eslint-disable-next-line i18next/no-literal-string
              timeZone="Europe/Helsinki"
              // eslint-disable-next-line i18next/no-literal-string
              locale="fi"
              classNames={{
                ...defaultClassNames,
                // eslint-disable-next-line i18next/no-literal-string
                today: 'border-violet-600/50 border web:hover:bg-purple-700',
                today_label: 'text-purple-300',
                selected:
                  // eslint-disable-next-line i18next/no-literal-string
                  'bg-purple-600 border-purple-400 web:hover:bg-purple-700',
                selected_label: 'text-white',
                day: cn(defaultClassNames.day, 'web:hover:bg-purple-800/50'),
                disabled: 'opacity-50',
              }}
              components={{
                IconNext: <Icon name="chevron-forward" size={20} className="text-foreground" />,
                IconPrev: <Icon name="chevron-back" size={20} className="text-foreground" />,
              }}
            />

            <View className="mt-4 flex-row justify-between gap-2">
              <Button variant="outline" size="lg" onPress={handleCancel}>
                {t(($) => $.actions.cancel)}
              </Button>

              <Button variant="primary" size="lg" onPress={handleConfirm}>
                {t(($) => $.actions.select)}
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  )
}
